import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./ProjectCostReport.css";

const MIN_VALID_YEAR = 2020;
const PAGE_SIZE = 10;

const ProjectCostReport = () => {
  const [logs, setLogs] = useState([]);
  const [projectList, setProjectList] = useState([]);
  const [searchParams] = useSearchParams();

  const [project, setProject] = useState("");
  const [query, setQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [openedProject, setOpenedProject] = useState("");
  const [categoryVisibleCounts, setCategoryVisibleCounts] = useState({});

  const [rangeType, setRangeType] = useState("tillDate");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const inputRef = useRef(null);
  const suggRef = useRef(null);
  const navigate = useNavigate();

  const isValidDate = (value) => {
    if (!value) return false;
    const date = new Date(value);
    return (
      !Number.isNaN(date.getTime()) && date.getFullYear() >= MIN_VALID_YEAR
    );
  };

  const getMonthKey = (value) => {
    if (!isValidDate(value)) return "";
    const date = new Date(value);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
  };

  const monthKeyToLabel = (monthKey) => {
    if (!monthKey) return "-";
    const [year, month] = monthKey.split("-").map(Number);

    return new Date(year, month - 1, 1).toLocaleDateString("en-GB", {
      month: "short",
      year: "numeric",
    });
  };

  const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";

    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatINR = (value) =>
    Number(value || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const getLogHours = (log) => Number.parseFloat(log.actualHours) || 0;

  const getLogEmployeeName = (log) =>
    log.user?.name || log.userName || log.employeeName || "Unknown";

  const getLogRate = (log) => Number(log.hourlyRateAtEntry || 0);

  const getLogCost = (log) => {
    const hours = getLogHours(log);
    const rate = getLogRate(log);
    return Number((hours * rate).toFixed(2));
  };

  const getLogCategory = (log) => {
    const category = String(log.projectCategory || "UNCATEGORIZED").trim();

    return category || "UNCATEGORIZED";
  };

  const getCurrentMonthKey = () => getMonthKey(new Date());

  const getCurrentFYRange = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    const startYear = month >= 4 ? year : year - 1;

    return {
      from: `${startYear}-04`,
      to: `${startYear + 1}-03`,
    };
  };

  const subtractMonths = (monthKey, count) => {
    const [year, month] = monthKey.split("-").map(Number);
    const date = new Date(year, month - 1, 1);
    date.setMonth(date.getMonth() - count);

    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
  };

  useEffect(() => {
    const projectFromUrl = searchParams.get("project");

    if (projectFromUrl) {
      setProject(projectFromUrl);
      setQuery(projectFromUrl);
      setOpenedProject("");
      setShowSuggestions(false);
      setHighlight(-1);
    }
  }, [searchParams]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };

        const logsResponse = await api.get("/logs/all", { headers });
        const cleanLogs = (logsResponse.data || []).filter((log) =>
          isValidDate(log.date),
        );

        setLogs(cleanLogs);

        const projects = new Set();
        cleanLogs.forEach((log) => {
          const projectName = String(log.projectName || "").trim();
          if (projectName) projects.add(projectName);
        });

        setProjectList(Array.from(projects).sort());
      } catch (error) {
        console.error("Project cost report load error:", error);
        window.alert("Failed to load project cost data.");
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      const clickedInsideInput =
        inputRef.current && inputRef.current.contains(event.target);
      const clickedInsideSuggestions =
        suggRef.current && suggRef.current.contains(event.target);

      if (!clickedInsideInput && !clickedInsideSuggestions) {
        setShowSuggestions(false);
      }
    };

    window.addEventListener("mousedown", handleOutsideClick);
    return () => window.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const projectLogsAll = useMemo(() => {
    if (!project) return [];

    return logs.filter(
      (log) => String(log.projectName || "").trim() === project,
    );
  }, [logs, project]);

  const projectMonthBounds = useMemo(() => {
    if (!projectLogsAll.length) return { start: "", end: "" };

    const months = projectLogsAll
      .map((log) => getMonthKey(log.date))
      .filter(Boolean)
      .sort();

    return {
      start: months[0] || "",
      end: months[months.length - 1] || "",
    };
  }, [projectLogsAll]);

  const activeRange = useMemo(() => {
    const currentMonth = getCurrentMonthKey();

    if (rangeType === "fy") return getCurrentFYRange();

    if (rangeType === "last6") {
      return {
        from: subtractMonths(currentMonth, 5),
        to: currentMonth,
      };
    }

    if (rangeType === "custom") {
      return {
        from: customFrom || projectMonthBounds.start || "",
        to: customTo || currentMonth,
      };
    }

    return {
      from: projectMonthBounds.start || "",
      to: currentMonth,
    };
  }, [rangeType, customFrom, customTo, projectMonthBounds]);

  const rangeLabel = useMemo(() => {
    if (!activeRange.from && !activeRange.to) return "Till date";

    return `${monthKeyToLabel(activeRange.from)} to ${monthKeyToLabel(
      activeRange.to,
    )}`;
  }, [activeRange]);

  const allRangeLogs = useMemo(() => {
    return logs.filter((log) => {
      const monthKey = getMonthKey(log.date);
      if (!monthKey) return false;
      if (activeRange.from && monthKey < activeRange.from) return false;
      if (activeRange.to && monthKey > activeRange.to) return false;
      return true;
    });
  }, [logs, activeRange]);

  const categoryProjects = useMemo(() => {
    const grouped = {};

    allRangeLogs.forEach((log) => {
      const category = getLogCategory(log);
      const projectName = String(log.projectName || "").trim();

      if (!projectName) return;

      if (!grouped[category]) grouped[category] = {};

      if (!grouped[category][projectName]) {
        grouped[category][projectName] = {
          category,
          projectName,
          totalHours: 0,
          totalCost: 0,
          employees: new Set(),
          lastActivity: "",
        };
      }

      const row = grouped[category][projectName];
      row.totalHours += getLogHours(log);
      row.totalCost += getLogCost(log);
      row.employees.add(getLogEmployeeName(log));

      if (
        !row.lastActivity ||
        new Date(log.date) > new Date(row.lastActivity)
      ) {
        row.lastActivity = log.date;
      }
    });

    return Object.keys(grouped)
      .sort((a, b) => a.localeCompare(b))
      .map((category) => ({
        category,
        projects: Object.values(grouped[category])
          .map((row) => ({
            ...row,
            totalHours: Number(row.totalHours.toFixed(2)),
            totalCost: Number(row.totalCost.toFixed(2)),
            employeeCount: row.employees.size,
          }))
          .sort((a, b) => b.totalCost - a.totalCost),
      }))
      .filter((group) => group.projects.length > 0);
  }, [allRangeLogs]);

  const filteredProjects = useMemo(() => {
    const normalizedQuery = query.toLowerCase().trim();

    if (!normalizedQuery) return projectList.slice(0, 300);

    return projectList
      .filter((item) => item.toLowerCase().includes(normalizedQuery))
      .slice(0, 300);
  }, [projectList, query]);

  const getVisibleCount = (category) =>
    Number(categoryVisibleCounts[category] || PAGE_SIZE);

  const showMoreProjects = (category, totalProjects) => {
    setCategoryVisibleCounts((previous) => {
      const current = Number(previous[category] || PAGE_SIZE);

      return {
        ...previous,
        [category]: Math.min(current + PAGE_SIZE, totalProjects),
      };
    });
  };

  const showOnlyTopTen = (category) => {
    setCategoryVisibleCounts((previous) => ({
      ...previous,
      [category]: PAGE_SIZE,
    }));
  };

  const selectProject = (projectName) => {
    setProject(projectName);
    setQuery(projectName);
    setShowSuggestions(false);
    setHighlight(-1);
    setRangeType("tillDate");
    setCustomFrom("");
    setCustomTo("");
    setOpenedProject("");
  };

  const clearProject = () => {
    setProject("");
    setQuery("");
    setOpenedProject("");
    setShowSuggestions(false);
    setHighlight(-1);
    setRangeType("tillDate");
    setCustomFrom("");
    setCustomTo("");
  };

  const handleInputKeyDown = (event) => {
    if (!showSuggestions) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlight((current) =>
        Math.min(current + 1, filteredProjects.length - 1),
      );
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlight((current) => Math.max(current - 1, 0));
    } else if (event.key === "Enter") {
      if (highlight >= 0 && filteredProjects[highlight]) {
        selectProject(filteredProjects[highlight]);
      }
    } else if (event.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  const getProjectDetail = (category, projectName) => {
    const detailLogs = allRangeLogs.filter(
      (log) =>
        String(log.projectName || "").trim() === projectName &&
        getLogCategory(log) === category,
    );

    const monthlyMap = {};
    const employeeMap = {};

    detailLogs.forEach((log) => {
      const monthKey = getMonthKey(log.date);
      const employeeName = getLogEmployeeName(log);
      const rate = getLogRate(log);
      const employeeKey = `${employeeName}_${rate}`;
      const hours = getLogHours(log);
      const cost = getLogCost(log);

      if (monthKey) {
        monthlyMap[monthKey] = (monthlyMap[monthKey] || 0) + cost;
      }

      if (!employeeMap[employeeKey]) {
        employeeMap[employeeKey] = {
          name: employeeName,
          rate,
          hours: 0,
          cost: 0,
        };
      }

      employeeMap[employeeKey].hours += hours;
      employeeMap[employeeKey].cost += cost;
    });

    const monthlyRows = Object.keys(monthlyMap)
      .sort()
      .map((monthKey) => ({
        monthKey,
        monthLabel: monthKeyToLabel(monthKey),
        cost: Number(monthlyMap[monthKey].toFixed(2)),
      }));

    const employeeRows = Object.values(employeeMap)
      .map((row) => ({
        ...row,
        hours: Number(row.hours.toFixed(2)),
        cost: Number(row.cost.toFixed(2)),
      }))
      .sort((a, b) => a.name.localeCompare(b.name) || a.rate - b.rate);

    const totalHours = Number(
      detailLogs.reduce((sum, log) => sum + getLogHours(log), 0).toFixed(2),
    );

    const totalCost = Number(
      detailLogs.reduce((sum, log) => sum + getLogCost(log), 0).toFixed(2),
    );

    const dates = detailLogs
      .map((log) => new Date(log.date))
      .filter((date) => !Number.isNaN(date.getTime()))
      .sort((a, b) => a - b);

    const highestMonth = monthlyRows.length
      ? [...monthlyRows].sort((a, b) => b.cost - a.cost)[0]
      : { monthLabel: "-", cost: 0 };

    return {
      monthlyRows,
      employeeRows,
      totalHours,
      totalCost,
      firstDate: dates[0] || "",
      lastDate: dates[dates.length - 1] || "",
      averageMonth: monthlyRows.length ? totalCost / monthlyRows.length : 0,
      highestMonth,
    };
  };

  const exportCSV = () => {
    let csv = "Category-wise Highest Expense Projects\n";
    csv += `"Range","${rangeLabel}"\n\n`;
    csv +=
      "Category,Rank,Project Name,Total Hours,Expense,Employees,Last Activity\n";

    categoryProjects.forEach((group) => {
      group.projects.forEach((row, index) => {
        csv += `"${group.category}",${index + 1},"${row.projectName}",${
          row.totalHours
        },${row.totalCost},${row.employeeCount},"${formatDate(
          row.lastActivity,
        )}"\n`;
      });
    });

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");

    anchor.href = url;
    anchor.download = "Category_Wise_Project_Expense.csv";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <main className="pcr-page">
      <header className="pcr-page-header">
        <button
          type="button"
          className="pcr-back-btn"
          onClick={() => navigate(-1)}
        >
          ← Back
        </button>

        <div>
          <h1>Project Cost Report</h1>
          <p>Category-wise expense ranking and project contribution details</p>
        </div>
      </header>

      <section className="pcr-panel pcr-filter-panel">
        <div className="pcr-panel-title">Filters</div>

        <div className="pcr-filter-grid">
          <div className="pcr-project-filter">
            <label htmlFor="project-search">Project</label>

            <div className="pcr-search-shell" ref={inputRef}>
              <input
                id="project-search"
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setShowSuggestions(true);
                  setHighlight(-1);
                }}
                onFocus={() => setShowSuggestions(true)}
                onKeyDown={handleInputKeyDown}
                placeholder="Search & select project..."
              />

              {query && (
                <button
                  type="button"
                  className="pcr-search-clear"
                  onClick={clearProject}
                  aria-label="Clear project"
                >
                  ×
                </button>
              )}

              {showSuggestions && (
                <div className="pcr-suggestions" ref={suggRef}>
                  {filteredProjects.map((item, index) => (
                    <button
                      type="button"
                      key={item}
                      className={
                        index === highlight
                          ? "pcr-suggestion pcr-suggestion-active"
                          : "pcr-suggestion"
                      }
                      onMouseEnter={() => setHighlight(index)}
                      onClick={() => selectProject(item)}
                    >
                      {item}
                    </button>
                  ))}

                  {!filteredProjects.length && (
                    <div className="pcr-no-results">No matching projects</div>
                  )}
                </div>
              )}
            </div>

            {project && (
              <div className="pcr-selected-project">
                Selected: <strong>{project}</strong>
                <button type="button" onClick={clearProject}>
                  Clear
                </button>
              </div>
            )}
          </div>

          <div className="pcr-range-filter">
            <label>Date Range</label>

            <div className="pcr-range-buttons">
              {[
                ["tillDate", "Till Date"],
                ["fy", "Current FY"],
                ["last6", "Last 6 Months"],
                ["custom", "Custom Range"],
              ].map(([value, label]) => (
                <button
                  type="button"
                  key={value}
                  className={
                    rangeType === value
                      ? "pcr-range-btn pcr-range-btn-active"
                      : "pcr-range-btn"
                  }
                  onClick={() => setRangeType(value)}
                >
                  {label}
                </button>
              ))}
            </div>

            {rangeType === "custom" && (
              <div className="pcr-custom-range">
                <div>
                  <label htmlFor="from-month">From Month</label>
                  <input
                    id="from-month"
                    type="month"
                    value={customFrom}
                    onChange={(event) => setCustomFrom(event.target.value)}
                  />
                </div>

                <div>
                  <label htmlFor="to-month">To Month</label>
                  <input
                    id="to-month"
                    type="month"
                    value={customTo}
                    onChange={(event) => setCustomTo(event.target.value)}
                  />
                </div>
              </div>
            )}

            <div className="pcr-range-label">
              Showing: <strong>{rangeLabel}</strong>
            </div>
          </div>
        </div>

        <div className="pcr-filter-footer">
          <button type="button" className="pcr-export-btn" onClick={exportCSV}>
            Export CSV
          </button>
        </div>
      </section>

      <section className="pcr-panel pcr-ranking-panel">
        <div className="pcr-ranking-header">
          <div>
            <h2>Project Expense Ranking</h2>
            <p>Highest expense projects grouped by project category</p>
          </div>
        </div>

        {!categoryProjects.length ? (
          <div className="pcr-empty-state">
            No category-wise expense data found.
          </div>
        ) : (
          <div className="pcr-category-list">
            {categoryProjects.map((group) => {
              const visibleCount = Math.min(
                getVisibleCount(group.category),
                group.projects.length,
              );

              return (
                <article className="pcr-category-card" key={group.category}>
                  <div className="pcr-category-header">
                    <div className="pcr-category-main">
                      <span className="pcr-category-mark" />
                      <div>
                        <h3>{group.category}</h3>
                        <p>Highest Expense Projects</p>
                      </div>
                    </div>

                    <span className="pcr-count-chip">
                      Showing {visibleCount} / {group.projects.length}
                    </span>
                  </div>

                  <div className="pcr-table-container">
                    <table className="pcr-expense-table">
                      <thead>
                        <tr>
                          <th className="pcr-rank-column">Rank</th>
                          <th className="pcr-project-column">Project Name</th>
                          <th>Total Hours</th>
                          <th>Expense</th>
                          <th>Employees</th>
                          <th>Last Activity</th>
                          <th className="pcr-action-column">Action</th>
                        </tr>
                      </thead>

                      <tbody>
                        {group.projects
                          .slice(0, visibleCount)
                          .map((row, index) => {
                            const openKey = `${group.category}__${row.projectName}`;
                            const isOpen = openedProject === openKey;
                            const detail = isOpen
                              ? getProjectDetail(
                                  group.category,
                                  row.projectName,
                                )
                              : null;

                            return (
                              <React.Fragment key={openKey}>
                                <tr className={isOpen ? "pcr-row-open" : ""}>
                                  <td className="pcr-rank-cell">
                                    <span>#{index + 1}</span>
                                  </td>

                                  <td className="pcr-project-name-cell">
                                    {row.projectName}
                                  </td>

                                  <td>{row.totalHours.toFixed(2)}</td>

                                  <td className="pcr-expense-cell">
                                    ₹{formatINR(row.totalCost)}
                                  </td>

                                  <td>{row.employeeCount}</td>

                                  <td>{formatDate(row.lastActivity)}</td>

                                  <td>
                                    <button
                                      type="button"
                                      className={
                                        isOpen
                                          ? "pcr-open-btn pcr-open-btn-active"
                                          : "pcr-open-btn"
                                      }
                                      onClick={() => {
                                        setOpenedProject(isOpen ? "" : openKey);
                                        setProject(row.projectName);
                                        setQuery(row.projectName);
                                        setShowSuggestions(false);
                                        setHighlight(-1);
                                      }}
                                    >
                                      {isOpen ? "Close" : "Open"}
                                    </button>
                                  </td>
                                </tr>

                                {isOpen && detail && (
                                  <tr className="pcr-detail-row">
                                    <td colSpan={7}>
                                      <div className="pcr-inline-detail">
                                        <div className="pcr-kpi-grid">
                                          <div className="pcr-kpi-card">
                                            <span>Active months</span>
                                            <strong>
                                              {detail.monthlyRows.length}
                                            </strong>
                                          </div>

                                          <div className="pcr-kpi-card">
                                            <span>Total hours</span>
                                            <strong>
                                              {detail.totalHours.toFixed(2)}
                                            </strong>
                                          </div>

                                          <div className="pcr-kpi-card">
                                            <span>Avg / month</span>
                                            <strong>
                                              ₹{formatINR(detail.averageMonth)}
                                            </strong>
                                          </div>

                                          <div className="pcr-kpi-card">
                                            <span>Highest month</span>
                                            <strong>
                                              ₹
                                              {formatINR(
                                                detail.highestMonth.cost,
                                              )}
                                            </strong>
                                            <small>
                                              {detail.highestMonth.monthLabel}
                                            </small>
                                          </div>
                                        </div>

                                        <div className="pcr-detail-card pcr-total-card">
                                          <div className="pcr-detail-card-header">
                                            Project Cost Till Date
                                          </div>

                                          <div className="pcr-total-content">
                                            <div className="pcr-total-project">
                                              {row.projectName}
                                            </div>

                                            <div className="pcr-total-amount">
                                              ₹{formatINR(detail.totalCost)}
                                            </div>

                                            <p>
                                              {rangeLabel} •{" "}
                                              {detail.totalHours.toFixed(2)}{" "}
                                              total hours
                                            </p>

                                            <small>
                                              First Activity:{" "}
                                              {formatDate(detail.firstDate)} •
                                              Last Activity:{" "}
                                              {formatDate(detail.lastDate)}
                                            </small>
                                          </div>
                                        </div>

                                        <div className="pcr-detail-card">
                                          <div className="pcr-detail-card-header">
                                            Employee Contribution
                                            <span>
                                              Rate-wise hours and cost
                                            </span>
                                          </div>

                                          {!detail.employeeRows.length ? (
                                            <div className="pcr-empty-state">
                                              No employees found.
                                            </div>
                                          ) : (
                                            <div className="pcr-employee-table">
                                              <div className="pcr-employee-head">
                                                <div>Employee</div>
                                                <div>Rate Used</div>
                                                <div>Hours</div>
                                                <div>Cost</div>
                                              </div>

                                              {detail.employeeRows.map(
                                                (employee) => (
                                                  <div
                                                    className="pcr-employee-row"
                                                    key={`${employee.name}_${employee.rate}`}
                                                  >
                                                    <div className="pcr-employee-name">
                                                      {employee.name}
                                                    </div>

                                                    <div>
                                                      <span className="pcr-rate-chip">
                                                        ₹
                                                        {formatINR(
                                                          employee.rate,
                                                        )}
                                                        /hr
                                                      </span>
                                                    </div>

                                                    <div className="pcr-metric-cell">
                                                      <progress
                                                        max={
                                                          detail.totalHours || 1
                                                        }
                                                        value={employee.hours}
                                                      />
                                                      <span>
                                                        {employee.hours.toFixed(
                                                          2,
                                                        )}
                                                      </span>
                                                    </div>

                                                    <div className="pcr-metric-cell">
                                                      <progress
                                                        max={
                                                          detail.totalCost || 1
                                                        }
                                                        value={employee.cost}
                                                      />
                                                      <span>
                                                        ₹
                                                        {formatINR(
                                                          employee.cost,
                                                        )}
                                                      </span>
                                                    </div>
                                                  </div>
                                                ),
                                              )}

                                              <div className="pcr-employee-total">
                                                <div>Total</div>
                                                <div>—</div>
                                                <div>
                                                  {detail.totalHours.toFixed(2)}{" "}
                                                  hrs
                                                </div>
                                                <div>
                                                  ₹{formatINR(detail.totalCost)}
                                                </div>
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>

                  {group.projects.length > PAGE_SIZE && (
                    <div className="pcr-pagination-actions">
                      {visibleCount < group.projects.length ? (
                        <button
                          type="button"
                          className="pcr-view-more-btn"
                          onClick={() =>
                            showMoreProjects(
                              group.category,
                              group.projects.length,
                            )
                          }
                        >
                          View 10 More
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="pcr-view-less-btn"
                          onClick={() => showOnlyTopTen(group.category)}
                        >
                          Show Only Top 10
                        </button>
                      )}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </section>
    </main>
  );
};

export default ProjectCostReport;
